import { t } from "@lingui/macro";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import styled from "styled-components";
import SectionTitle from "~/guidelines/SectionTitle";
import mediaQueries from "~/guidelines/Theme/mediaQueries";
import { body1, heading3, heading4 } from "~/guidelines/Typography";
import Image1 from "../_assets/added-values-section/accompagnement.png";
import Image2 from "../_assets/added-values-section/gestion-cle-en-main.png";
import Image3 from "../_assets/added-values-section/impact-positif.png";
import Image4 from "../_assets/added-values-section/investissement-solides.png";

const SectionWrapper = styled.section`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
  padding: ${({ theme }) => theme.spacing(7, 5)};

  ${mediaQueries.lg()} {
    margin: 0 auto;
    gap: ${({ theme }) => theme.spacing(8)};
    padding: ${({ theme }) => theme.spacing(13, 0)};
  }
`;

const DesktopOnly = styled.div`
  display: none;
  width: 100%;
  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    display: flex;
    gap: ${({ theme }) => theme.spacing(8)};
  }
`;

const MobileOnly = styled.div`
  display: block;
  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    display: none;
  }
`;

// --- Desktop Layout ---
const LeftMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 40%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(8)};
`;

const MenuItem = styled.li<{ active: boolean }>`
  ${heading4}
  color: ${({ active, theme }) =>
    active ? theme.color.text.lightTertiary : theme.color.text.lightFirst};
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: color 0.2s;
`;

const ContentBlock = styled.div`
  background: ${({ theme }) => theme.color.background.lightSecond};
  flex: 1;
  height: 520px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ImageStyled = styled.img`
  height: auto;
  object-fit: cover;
  max-height: 370px;
`;

const Description = styled.p`
  ${body1}
  text-align: left;
  max-width: 70%;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    text-align: center;
  }
`;

// --- Mobile Layout ---
const MobileBlock = styled.div``;

const MobileTitle = styled.h3<{ active: boolean }>`
  ${heading3};
  color: ${({ active, theme }) =>
    active ? theme.color.text.lightTertiary : theme.color.text.lightFirst};
`;

const AddedValuesSection: React.FC = () => {
  const [displayedIndex, setDisplayedIndex] = useState(0);

  const VALUES = [
    {
      key: "assistance",
      title: t`Assistance`,
      description: t`Sourcing opportunities, works, furniture and decoration`,
      image: Image1,
    },
    {
      key: "turnkey",
      title: t`Turnkey management`,
      description: t`Complete management of the establishment, offering operation without constraints for the owner`,
      image: Image2,
    },
    {
      key: "positive-impact",
      title: t`Positive impact`,
      description: t`(Exclusively) rehabilitation projects to meet the climate challenges of today and tomorrow`,
      image: Image3,
    },
    {
      key: "solid-investments",
      title: t`Solid investments`,
      description: t`Investments with high value creation guaranteeing a competitive return`,
      image: Image4,
    },
  ];

  const handleMenuClick = (index: number) => {
    if (index === displayedIndex) return;
    setDisplayedIndex(index);
  };

  return (
    <SectionWrapper>
      <SectionTitle
        variant="normal"
        firstPart={t`our_plural`}
        secondPart={t`Added values`}
        center="all"
      />

      <DesktopOnly>
        <LeftMenu>
          {VALUES.map((valeur, idx) => (
            <MenuItem
              key={valeur.key}
              active={displayedIndex === idx}
              onClick={() => handleMenuClick(idx)}
            >
              {valeur.title}
            </MenuItem>
          ))}
        </LeftMenu>

        <AnimatePresence mode="wait">
          <motion.div
            key={displayedIndex}
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            style={{ width: "100%", height: "100%" }}
          >
            <ContentBlock>
              <ImageStyled
                src={VALUES[displayedIndex].image.src}
                alt={VALUES[displayedIndex].title}
              />
              <Description>{VALUES[displayedIndex].description}</Description>
            </ContentBlock>
          </motion.div>
        </AnimatePresence>
      </DesktopOnly>

      <MobileOnly>
        {VALUES.map((valeur) => (
          <MobileBlock key={valeur.key}>
            <MobileTitle active={0 === VALUES.indexOf(valeur)}>
              {valeur.title}
            </MobileTitle>

            <Description>{valeur.description}</Description>
          </MobileBlock>
        ))}
      </MobileOnly>
    </SectionWrapper>
  );
};

export default AddedValuesSection;
